import React, { useEffect, useRef, useState } from "react";
import {
  CalendarDays,
  ExternalLink,
  FileText,
  LayoutTemplate,
  LogOut,
  Mail,
  Menu,
  MessageCircle,
  Moon,
  Printer,
  Shield,
  Store,
  Sun,
  X,
} from "lucide-react";
import { Badge, Button, ICON, IconButton, cx } from "./ui";
import type { AdminTheme } from "./theme";
import type { AdminRole, AdminView } from "./views";

type IconComponent = React.ComponentType<{ size?: number; strokeWidth?: number }>;
type NavItem = { view: AdminView; label: string; icon: IconComponent };

/** Lo de todos los meses: pestañas de la barra y primeras entradas del menú (§1.1, §1.7). */
const MAIN_ITEMS: Record<AdminRole, NavItem[]> = {
  super: [
    { view: "turnos", label: "Turnos", icon: CalendarDays },
    { view: "carteles", label: "Carteles", icon: Printer },
  ],
  turnos: [
    { view: "turnos", label: "Turnos", icon: CalendarDays },
    { view: "cfg:farmacias", label: "Farmacias", icon: Store },
  ],
};

/** Configuración, solo superadmin (§1.3). */
const CONFIG_ITEMS: NavItem[] = [
  { view: "cfg:farmacias", label: "Farmacias", icon: Store },
  { view: "cfg:plantillas", label: "Plantillas y letras", icon: FileText },
  { view: "cfg:portada", label: "Portada del sitio", icon: LayoutTemplate },
  { view: "cfg:bot", label: "Bot de WhatsApp", icon: MessageCircle },
  { view: "cfg:correos", label: "Correos", icon: Mail },
  { view: "cfg:accesos", label: "Accesos", icon: Shield },
];

const ROLE_LABEL: Record<AdminRole, string> = {
  super: "Superadmin",
  turnos: "Admin de turnos",
};

function Wordmark({ inTopBar }: { inTopBar?: boolean }) {
  return (
    <span
      className={cx(
        "flex items-center gap-2 whitespace-nowrap text-[17px] font-semibold tracking-[-.03em]",
        inTopBar && "max-md:text-base",
      )}
    >
      <span className="size-2 rounded-full bg-adm-acento" aria-hidden="true" />
      Turnos Tandil
    </span>
  );
}

type AdminShellProps = {
  view: AdminView;
  onNavigate: (view: AdminView) => void;
  role: AdminRole;
  user: { email: string; name: string; picture?: string };
  onLogout: () => void;
  unreadEmails?: number;
  theme: AdminTheme;
  onToggleTheme: () => void;
  children?: React.ReactNode;
};

export function AdminShell({
  view,
  onNavigate,
  role,
  user,
  onLogout,
  unreadEmails = 0,
  theme,
  onToggleTheme,
  children,
}: AdminShellProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);

  // Esc cierra el menú; el foco entra al abrir y vuelve a la hamburguesa al cerrar (§1.8).
  useEffect(() => {
    if (menuOpen) {
      wasOpen.current = true;
      closeButtonRef.current?.focus();
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") setMenuOpen(false);
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    }
    if (wasOpen.current) {
      wasOpen.current = false;
      menuButtonRef.current?.focus();
    }
  }, [menuOpen]);

  const go = (next: AdminView) => {
    setMenuOpen(false);
    onNavigate(next);
    window.scrollTo(0, 0);
  };

  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();

  const navButton = (item: NavItem) => {
    const on = view === item.view;
    const Icon = item.icon;
    return (
      <button
        key={item.view}
        type="button"
        aria-current={on ? "page" : undefined}
        onClick={() => go(item.view)}
        className={cx(
          "flex h-[42px] w-full items-center gap-3 rounded-[10px] px-3 text-left text-sm transition-colors cursor-pointer max-md:h-11",
          on
            ? "bg-adm-acento-suave font-medium text-adm-acento-texto"
            : "bg-transparent font-[450] text-adm-t70 hover:bg-adm-t5 hover:text-adm-text",
        )}
      >
        <Icon {...ICON} />
        {item.label}
        {item.view === "cfg:correos" && unreadEmails > 0 && (
          <Badge tone="ok" className="ml-auto">
            {unreadEmails}
          </Badge>
        )}
      </button>
    );
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-[60] flex h-[60px] items-center gap-3 border-b border-adm-border bg-adm-bg/88 px-4 backdrop-blur-[10px] max-md:gap-1 max-md:pl-1 max-md:pr-2">
        <IconButton
          ref={menuButtonRef}
          label="Abrir menú"
          aria-controls="admin-side"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        >
          <Menu {...ICON} />
        </IconButton>
        <Wordmark inTopBar />
        <nav className="ml-3 flex gap-0.5 max-md:ml-auto" aria-label="Lo de todos los meses">
          {MAIN_ITEMS[role].map((item) => {
            const on = view === item.view;
            const Icon = item.icon;
            return (
              <button
                key={item.view}
                type="button"
                aria-current={on ? "page" : undefined}
                aria-label={item.label}
                onClick={() => go(item.view)}
                className={cx(
                  "inline-flex h-9 items-center gap-2 rounded-[10px] px-3.5 text-sm font-medium transition-colors cursor-pointer max-md:h-11 max-md:px-2.5",
                  on
                    ? "bg-adm-acento-suave text-adm-acento-texto"
                    : "bg-transparent text-adm-t55 hover:bg-adm-t5 hover:text-adm-text",
                )}
              >
                <Icon {...ICON} />
                <span className="max-md:hidden">{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="ml-auto flex items-center gap-1 max-md:ml-0">
          <IconButton label="Cambiar tema" onClick={onToggleTheme}>
            {theme === "dark" ? <Sun {...ICON} /> : <Moon {...ICON} />}
          </IconButton>
        </div>
      </header>

      {menuOpen && (
        <div
          className="fixed inset-0 z-[90] bg-adm-overlay backdrop-blur-[3px]"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        id="admin-side"
        aria-label="Menú"
        inert={!menuOpen}
        className={cx(
          "fixed inset-y-0 left-0 z-[100] flex w-[288px] max-w-[86vw] flex-col border-r border-adm-border bg-adm-surface shadow-adm transition-transform duration-[280ms] ease-[cubic-bezier(.2,.8,.2,1)]",
          menuOpen ? "translate-x-0" : "-translate-x-[102%]",
        )}
      >
        <div className="flex h-[60px] items-center justify-between border-b border-adm-border pl-5 pr-3">
          <Wordmark />
          <IconButton ref={closeButtonRef} label="Cerrar menú" onClick={() => setMenuOpen(false)}>
            <X {...ICON} />
          </IconButton>
        </div>
        <nav className="flex flex-1 flex-col gap-0.5 overflow-auto p-3">
          {MAIN_ITEMS[role].map(navButton)}
          {role === "super" && (
            <>
              <div className="px-2.5 pb-1.5 pt-4 text-xs font-medium text-adm-t55">Configuración</div>
              {CONFIG_ITEMS.map(navButton)}
            </>
          )}
        </nav>
        <div className="flex flex-col gap-2.5 border-t border-adm-border px-4 py-3.5">
          <Button size="sm" block href="/" target="_blank" icon={<ExternalLink {...ICON} />}>
            Ver la web pública
          </Button>
          <div className="flex items-center gap-2.5">
            <div
              className="grid size-8 shrink-0 place-items-center rounded-full bg-adm-acento-suave text-[13px] font-semibold text-adm-acento-texto"
              aria-hidden="true"
            >
              {initial}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-medium" title={user.email}>
                {user.name}
              </div>
              <div className="text-xs text-adm-t55">{ROLE_LABEL[role]}</div>
            </div>
            <IconButton label="Salir" tone="danger" onClick={onLogout}>
              <LogOut {...ICON} />
            </IconButton>
          </div>
        </div>
      </aside>

      <main className="px-8 pb-16 pt-9 max-md:px-4 max-md:pb-12 max-md:pt-5">
        <div className="mx-auto w-full max-w-[1160px]">{children}</div>
      </main>
    </div>
  );
}
